import React, { useState, useEffect } from 'react';
import { Button } from '../ui/button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../lib/auth/AuthContext';
import { UserManagement } from './UserManagement';
import { ProjectList } from './project/ProjectList';
import { LanguageManager } from './LanguageManager';
import { LeadManagement } from './LeadManagement';
import { VisitorAnalytics } from './VisitorAnalytics';

export const Dashboard: React.FC = () => {
  const { logout, user, useAuthenticatedQuery } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  
  // Use authenticated query
  const languages = useAuthenticatedQuery('languages:list', { activeOnly: false }) || [];
  const [isLoadingLanguages, setIsLoadingLanguages] = useState(true);
  
  // Set loading state after a timeout to indicate we've waited for languages to load
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoadingLanguages(false);
    }, 2000);
    
    return () => clearTimeout(timer);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'users':
        return <UserManagement />;
      case 'projects':
        return <ProjectList />;
      case 'languages':
        return <LanguageManager />;
      case 'leads':
        return <LeadManagement />;
      case 'analytics':
        return <VisitorAnalytics />;
      default:
        return (
          <div className="p-6">
            <h2 className="text-2xl font-bold mb-4">Welcome, {user?.name}</h2>
            <p className="text-gray-600 mb-8">Role: {user?.role?.name || 'No Role'}</p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {user?.role?.permissions?.includes('manage_users') && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-2">User Management</h3>
                  <p className="text-gray-600 mb-4">Manage system users and their permissions</p>
                  <Button onClick={() => setActiveTab('users')}>Manage Users</Button>
                </div>
              )}

              {user?.role?.permissions?.includes('manage_projects') && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-2">Project Management</h3>
                  <p className="text-gray-600 mb-4">Create and configure landing pages for projects</p>
                  <Button onClick={() => setActiveTab('projects')}>Manage Projects</Button>
                </div>
              )}

              {(user?.role?.permissions?.includes('manage_leads') || user?.role?.permissions?.includes('view_leads')) && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-2">Lead Management</h3>
                  <p className="text-gray-600 mb-4">View and manage customer leads from your projects</p>
                  <Button onClick={() => setActiveTab('leads')}>Manage Leads</Button>
                </div>
              )}

              {(user?.role?.permissions?.includes('view_analytics') || user?.role?.permissions?.includes('manage_leads')) && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-2">Visitor Analytics</h3>
                  <p className="text-gray-600 mb-4">Track visitor sources, campaigns, and conversion metrics</p>
                  <Button onClick={() => setActiveTab('analytics')}>View Analytics</Button>
                </div>
              )}

              {user?.role?.permissions?.includes('manage_settings') && (
                <div className="bg-white rounded-lg shadow p-6">
                  <h3 className="text-lg font-semibold mb-2">Language Management</h3>
                  <p className="text-gray-600 mb-4">Configure languages for your landing pages</p>
                  <div className="text-sm mb-4">
                    {isLoadingLanguages ? (
                      <p className="text-blue-600">Loading language settings...</p>
                    ) : languages.length > 0 ? (
                      <p className="text-green-600">Languages are configured: {languages.length} languages available</p>
                    ) : (
                      <p className="text-amber-600">No languages configured yet. Please add languages.</p>
                    )}
                  </div>
                  <Button onClick={() => setActiveTab('languages')}>Manage Languages</Button>
                </div>
              )}
            </div>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <nav className="bg-white shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
            </div>
            <div className="flex items-center space-x-4">
              <Button variant="ghost" onClick={() => setActiveTab('dashboard')}>
                Dashboard
              </Button>
              {user?.role?.permissions?.includes('manage_users') && (
                <Button variant="ghost" onClick={() => setActiveTab('users')}>
                  Users
                </Button>
              )}
              {user?.role?.permissions?.includes('manage_projects') && (
                <Button variant="ghost" onClick={() => setActiveTab('projects')}>
                  Projects
                </Button>
              )}
              {(user?.role?.permissions?.includes('manage_leads') || user?.role?.permissions?.includes('view_leads')) && (
                <Button variant="ghost" onClick={() => setActiveTab('leads')}>
                  Leads
                </Button>
              )}
              {(user?.role?.permissions?.includes('view_analytics') || user?.role?.permissions?.includes('manage_leads')) && (
                <Button variant="ghost" onClick={() => setActiveTab('analytics')}>
                  Analytics
                </Button>
              )}
              {user?.role?.permissions?.includes('manage_settings') && (
                <Button variant="ghost" onClick={() => setActiveTab('languages')}>
                  Languages
                </Button>
              )}
              <Button onClick={handleLogout}>Logout</Button>
            </div>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {renderContent()}
      </main>
    </div>
  );
}; 